// Guest Data Migration Utility
// Migrates guest user data to authenticated user after registration

import { logger } from './logger';
import { cloudSave } from '../services/cloudSave';
import { aiCoach } from '../services/aiCoach';

/**
 * Migrate all guest data to authenticated user
 * This ensures users don't lose progress when they register
 */
export async function migrateGuestDataToUser(userId: string): Promise<void> {
  try {
    logger.debug('Starting guest data migration for user:', userId);

    // Get all guest data from localStorage
    const testHistory = aiCoach.getTestHistory();
    const studyTime = aiCoach.getStudyTime();
    const averageScore = aiCoach.getCombinedAverage();
    
    // Get gamification data
    const gamificationData = localStorage.getItem('gamificationData');
    const dailyChallengesData = localStorage.getItem('dailyChallenges');
    const questionTrackerData = localStorage.getItem('questionTracker');
    const testCountData = localStorage.getItem('practiceTestCount');

    // Migrate test history (non-blocking batch operation)
    // Netflix/Duolingo pattern: Batch operations, don't await each one
    if (testHistory && testHistory.length > 0) {
      logger.debug(`Migrating ${testHistory.length} test results`);
      // Batch save operations - don't await each one (non-blocking)
      const savePromises = testHistory.map(result => 
        aiCoach.saveTestResult(result).catch(err => {
          logger.warn('Failed to save individual test result (non-critical):', err);
          return null; // Continue even if one fails
        })
      );
      // Wait for all saves in parallel (faster than sequential)
      await Promise.allSettled(savePromises);
    }

    // Migrate gamification data
    if (gamificationData) {
      try {
        const parsed = JSON.parse(gamificationData);
        // Update user ID in gamification data
        parsed.userId = userId;
        localStorage.setItem(`gamificationData_${userId}`, JSON.stringify(parsed));
        logger.debug('Migrated gamification data');
      } catch (e) {
        logger.warn('Failed to migrate gamification data:', e);
      }
    }

    // Migrate daily challenges
    if (dailyChallengesData) {
      try {
        localStorage.setItem(`dailyChallenges_${userId}`, dailyChallengesData);
        logger.debug('Migrated daily challenges data');
      } catch (e) {
        logger.warn('Failed to migrate daily challenges:', e);
      }
    }

    // Migrate question tracker
    if (questionTrackerData) {
      try {
        localStorage.setItem(`questionTracker_${userId}`, questionTrackerData);
        logger.debug('Migrated question tracker data');
      } catch (e) {
        logger.warn('Failed to migrate question tracker:', e);
      }
    }

    // Migrate test count (preserve soft gate status)
    if (testCountData) {
      try {
        localStorage.setItem(`practiceTestCount_${userId}`, testCountData);
        logger.debug('Migrated test count data');
      } catch (e) {
        logger.warn('Failed to migrate test count:', e);
      }
    }

    // Sync to cloud (non-blocking - don't wait for network)
    // Netflix/Duolingo pattern: Fire and forget for better UX
    const progressData = {
      testHistory,
      studyTime,
      averageScore
    };
    
    // Don't await - let it sync in background
    cloudSave.saveProgress(userId, progressData).catch(err => {
      logger.warn('Background cloud sync failed (non-critical, will retry):', err);
      // Cloud sync can retry later - user can still use the app
    });
    
    logger.debug('Guest data migration completed successfully');

  } catch (error) {
    logger.error('Guest data migration failed:', error);
    // Don't throw - allow registration to complete even if migration fails
    // Data is still in localStorage and can be synced later
  }
}








